/**
 * SubscribeUserSheetContent
 *
 * Content rendered inside the SubscribeUserSheet (via SheetService).
 * Mirrors Flutter's `SubscribeUserBottomSheet`
 * (`lib/presentation/bottom_sheet/subscribe_user_bottom_sheet.dart`):
 *   - Dynamic title that depends on subscription status / approval flow.
 *   - Centered price row (free or coins).
 *   - Multi-line description (preserves `\n\n` paragraph breaks via
 *     `whiteSpace: 'pre-line'`).
 *   - Plain-text policy disclaimer (link is rendered as text — the policy
 *     screen port is a separate ticket).
 *   - Test-mode auto-renew disclaimer when remote config IS_TEST_MODE is true.
 *   - Sticky-bottom CTA row (positive + close).
 *   - Semi-transparent loading overlay during fetch / subscribe action.
 *
 * The "title" prop on `SheetService.show()` is intentionally empty — we render
 * the title here so it can change reactively as the hook resolves. This
 * matches the `UnlockContentSheet` pattern.
 */
import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import { useSubscribeUser } from './hooks/useSubscribeUser';
import { SubscribeUserPriceRow } from './SubscribeUserPriceRow';
import { SubscribeUserDisclaimers } from './SubscribeUserDisclaimers';
import { SheetLoadingOverlay } from '@/presentation/components/common/loadingIndicators/SheetLoadingOverlay';
import { SubscribeUserCTAFooter } from './SubscribeUserCTAFooter';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface SubscribeUserSheetContentProps {
  targetFirebaseId: string;
  preloadedUser?: UserEntity | null;
  screenKey?: string;
}

export const SubscribeUserSheetContent: React.FC<SubscribeUserSheetContentProps> = ({
  targetFirebaseId,
  preloadedUser,
  screenKey,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  const {
    title,
    description,
    positiveText,
    showCoinsCharge,
    priceText,
    fetching,
    isSubscribing,
    onPositive,
    onClose,
  } = useSubscribeUser({ targetFirebaseId, preloadedUser, screenKey });

  const showOverlay = fetching || isSubscribing;

  return (
    <div
      {...qaAttr({ id: 'subscribe-user-sheet', status: showOverlay ? 'processing' : 'ready' })}
      className="flex flex-col"
      style={{ position: 'relative' }}
    >
      <div
        className="flex flex-col"
        style={{
          padding: responsive.spacing['2xl'],
          gap: responsive.spacing.lg,
        }}
      >
        {/* Title (rendered inside content so it can react to hook state) */}
        <span
          style={{
            color: theme.text.primary,
            fontSize: responsive.font.lg,
            fontWeight: responsive.fontWeight.semibold,
            textAlign: 'center',
          }}
        >
          {title}
        </span>

        {/* Price row */}
        <SubscribeUserPriceRow
          showCoinsCharge={showCoinsCharge}
          priceText={priceText}
        />

        {/* Description — preserves \n\n paragraph breaks from l10n strings */}
        <span
          style={{
            color: theme.text.secondary,
            fontSize: responsive.font.sm,
            textAlign: 'center',
            whiteSpace: 'pre-line',
            paddingLeft: responsive.spacing['2xl'],
            paddingRight: responsive.spacing['2xl'],
          }}
        >
          {description}
        </span>

        {/* Policy + test-mode disclaimers */}
        <SubscribeUserDisclaimers />
      </div>

      {/* CTA buttons (sticky bottom) */}
      <SubscribeUserCTAFooter
        positiveText={positiveText}
        isSubscribing={isSubscribing}
        fetching={fetching}
        onPositive={onPositive}
        onClose={onClose}
      />

      {showOverlay && <SheetLoadingOverlay qaId="subscribe-user-overlay" />}
    </div>
  );
};
